/**
 * ActivityTypeSelector Component
 *
 * Reusable activity type selection component for search filters.
 * Supports both desktop dropdown and mobile inline layouts.
 *
 * SOLID Compliance:
 * - SRP: Only handles activity type selection UI
 * - OCP: Extensible via variants (dropdown, inline)
 * - DIP: Depends on abstractions (onChange callback, not URLStateManager directly)
 *
 * Usage:
 * <ActivityTypeSelector
 *   value={{ slug: 'hiking', name: 'Hiking' }}
 *   onChange={(slug, name) => updateFilter('activitySlug', slug)}
 *   variant="dropdown" // or "inline"
 *   onClose={() => setShowDropdown(false)}
 * />
 *
 * Location: apps/web/src/components/search/ActivityTypeSelector.jsx
 * Created: 2025-10-31
 * Updated: 2025-11-26 - MVP 1.3 Phase 2 - Integrated with taxonomy API
 * Part of: MVP 1.9 - Adaptive Search & Filter System
 */

import React, { useState, useEffect } from 'react';
import { useTheme } from '../../hooks/useTheme';
import { getAllL1Categories } from '../../services/taxonomyService';
import {
  Fish,
  Crosshair,
  Mountain as _Mountain,
  Waves,
  Bike,
  Search,
  GraduationCap,
  Heart,
  Footprints,
  MapPin as _MapPin,
  Zap,
  Snowflake,
  Compass
} from 'lucide-react';

/**
 * Map icon_name from API to Lucide icon component
 * Icons match the taxonomy database icon_name field
 */
const ICON_MAP = {
  fish: Fish,
  crosshair: Crosshair,
  mountain: _Mountain,
  water: Waves,
  bicycle: Bike,
  binoculars: Search, // Binoculars not available, using Search as alternative
  'graduation-cap': GraduationCap,
  heart: Heart,
  hiking: Footprints,
  landmark: _MapPin, // Using MapPin for landmark
  lightning: Zap,
  snowflake: Snowflake
};

/**
 * ActivityTypeSelector Component
 * @param {Array} value - Selected activities: [{ slug, name }, ...]
 * @param {Function} onChange - Callback when selection changes: onChange(activities)
 * @param {string} variant - Display variant: 'dropdown' (desktop) or 'inline' (mobile)
 * @param {Function} onClose - Callback to close dropdown (for dropdown variant)
 */
export default function ActivityTypeSelector({
  value = [],
  onChange = () => {},
  variant = 'dropdown',
  onClose: _onClose = () => {}
}) {
  const theme = useTheme();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetch L1 categories from taxonomy API
   */
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getAllL1Categories();

        // Map API response to component format
        const mappedCategories = response.map(cat => ({
          id: cat.slug, // Use slug as ID for URL-friendly routing
          categoryId: cat.id, // Store actual database ID for filtering
          label: cat.name,
          icon: ICON_MAP[cat.icon_name] || Compass // Fallback to Compass icon
        }));

        setCategories(mappedCategories);
      } catch (err) {
        console.error('[ActivityTypeSelector] Failed to fetch categories:', err);
        setError('Failed to load activity types');
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Normalize value to array
  const selectedActivities = Array.isArray(value) ? value : [];
  const selectedSlugs = selectedActivities.map(a => a.slug);

  /**
   * Toggle type selection (multi-select)
   */
  const handleToggle = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);

    if (!category) {return;}

    // Check if already selected
    const isSelected = selectedSlugs.includes(categoryId);

    if (isSelected) {
      // Remove from selection
      const updated = selectedActivities.filter(a => a.slug !== categoryId);
      onChange(updated);
    } else {
      // Add to selection
      const updated = [
        ...selectedActivities,
        {
          slug: categoryId, // URL-friendly slug
          name: category.label, // Display name
          id: category.categoryId // Database ID for API filtering
        }
      ];
      onChange(updated);
    }
  };

  /**
   * Clear selection
   */
  const handleClear = () => {
    onChange([]);
  };

  /**
   * Render icon (emoji or Lucide icon component)
   */
  const renderIcon = (icon) => {
    if (typeof icon === 'string') {
      return <span className="text-2xl">{icon}</span>;
    }
    if (icon && (typeof icon === 'function' || typeof icon === 'object')) {
      const IconComponent = icon;
      return <IconComponent className={`w-6 h-6 ${theme.iconColor}`} />;
    }
    return null;
  };

  /**
   * Render category list (reusable for both variants)
   */
  const renderCategories = () => {
    // Loading state
    if (loading) {
      return (
        <div className="col-span-2 flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      );
    }

    // Error state
    if (error) {
      return (
        <div className="col-span-2 text-center p-4 text-red-600">
          {error}
        </div>
      );
    }

    // Empty state
    if (categories.length === 0) {
      return (
        <div className="col-span-2 text-center p-4 text-gray-500">
          No activity types available
        </div>
      );
    }

    // Render categories
    return categories.map((category) => {
      const isSelected = selectedSlugs.includes(category.id);

      return (
        <button
          key={category.id}
          onClick={() => handleToggle(category.id)}
          className={`flex items-center gap-3 p-3 rounded-lg transition hover:bg-gray-50 ${
            isSelected ? theme.bgLight : ''
          }`}
        >
          <div className="flex-shrink-0">
            {renderIcon(category.icon)}
          </div>
          <span className={`text-sm font-medium flex-1 text-left ${
            isSelected ? theme.textPrimary : 'text-gray-700'
          }`}>
            {category.label}
          </span>
          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition ${
            isSelected
              ? `${theme.borderPrimary} ${theme.buttonPrimary.split(' ')[0]}`
              : 'border-gray-300'
          }`}>
            {isSelected && (
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
        </button>
      );
    });
  };

  // Dropdown variant (desktop)
  if (variant === 'dropdown') {
    return (
      <div className="bg-white rounded-xl shadow-2xl border border-gray-200 p-4 min-w-96 w-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Select Adventure Type</h3>
          {selectedActivities.length > 0 && (
            <button
              onClick={handleClear}
              className={`text-xs ${theme.textPrimary} ${theme.textHover} font-medium`}
            >
              Clear
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2 max-h-96 overflow-y-auto">
          {renderCategories()}
        </div>
      </div>
    );
  }

  // Inline variant (mobile)
  return (
    <div className="grid grid-cols-2 gap-2">
      {renderCategories()}
    </div>
  );
}
